// /src/features/ui/uiSlice.js
import { createSlice } from '@reduxjs/toolkit';

// Helper function to get initial state from localStorage
const getInitialSidebarState = () => {
  try {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  } catch (error) {
    console.warn('Failed to load sidebar state from localStorage:', error);
    return false;
  }
};

// Helper function to save state to localStorage
const saveSidebarState = (collapsed) => {
  try {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  } catch (error) {
    console.warn('Failed to save sidebar state to localStorage:', error);
  }
};

// Global copy function
window.copyToClipboard = async (text, options = {}) => {
  const {
    onSuccess = () => {},
    onError = () => {},
    successMessage = 'Copied to clipboard!',
    errorMessage = 'Failed to copy to clipboard'
  } = options;

  try {
    // Use the modern clipboard API
    await navigator.clipboard.writeText(text);

    // Call success callback
    onSuccess(successMessage);

    return { success: true, message: successMessage };
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);

    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      // Call success callback even with fallback
      onSuccess(successMessage);

      return { success: true, message: successMessage };
    } catch (fallbackError) {
      console.error('Fallback copy also failed:', fallbackError);

      // Call error callback
      onError(errorMessage);

      return { success: false, message: errorMessage };
    }
  }
};

// Global copy code block function specifically for rich text content
// NOW ACCEPTS copyButton, copyIcon, and checkIcon directly
window.copyCodeBlock = async (codeElement, copyButton, copyIcon, checkIcon) => {
  try {
    // Get the text content of the code block
    const codeText = codeElement.textContent || codeElement.innerText;

    // Use the global copy function
    const result = await window.copyToClipboard(codeText);

    if (result.success && copyButton) {
      // Show success state using the passed elements
      if (copyIcon) copyIcon.style.display = 'none';
      if (checkIcon) checkIcon.style.display = 'inline'; // Changed to inline to match Ant Design icon default display

      copyButton.style.backgroundColor = '#10b981';
      copyButton.style.borderColor = '#10b981';
      copyButton.style.color = '#ffffff';

      // Reset after 2 seconds
      setTimeout(() => {
        if (copyIcon) copyIcon.style.display = 'inline'; // Changed to inline
        if (checkIcon) checkIcon.style.display = 'none';

        copyButton.style.backgroundColor = 'rgba(0,0,0,0.2)';
        copyButton.style.borderColor = 'transparent';
        copyButton.style.color = 'rgba(255,255,255,0.8)';
      }, 2000);
    }

    return result;
  } catch (error) {
    console.error('Error in copyCodeBlock:', error);
    return { success: false, message: 'Failed to copy code' };
  }
};

const initialState = {
  sidebarCollapsed: getInitialSidebarState(),
  copyState: {
    lastCopied: null,
    copyCount: 0
  }
  // You can add more UI states here in the future
  // theme: 'dark',
  // notifications: true,
  // etc.
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      saveSidebarState(state.sidebarCollapsed);
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload;
      saveSidebarState(state.sidebarCollapsed);
    },
    incrementCopyCount: (state) => {
      state.copyState.copyCount += 1;
      state.copyState.lastCopied = new Date().toISOString();
    },
    resetCopyCount: (state) => {
      state.copyState.copyCount = 0;
      state.copyState.lastCopied = null;
    }
    // Future UI actions can be added here
    // setTheme: (state, action) => {
    //   state.theme = action.payload;
    // },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  incrementCopyCount,
  resetCopyCount
} = uiSlice.actions;

// Selectors
export const selectSidebarCollapsed = (state) => state.ui.sidebarCollapsed;
export const selectCopyState = (state) => state.ui.copyState;

export default uiSlice.reducer;